// src/component/CommunityFeedNoImage.js
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  IconButton,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Link as MuiLink,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  useMediaQuery,
  Chip,
  Slide,
  Snackbar,
  Alert,
  FormControl,
  Select,
  Checkbox,
  InputLabel,
  FormControlLabel,
} from "@mui/material";

import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";

import { AppThemeContext } from "./AppShell";
import { useAuth } from "../context/auth";
import { useDayBoundary } from "../context/DayBoundaryContext";
import { db } from "../api/firebase";

import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  increment,
  deleteField,
  getDoc,
  getDocs,
  deleteDoc,
  startAfter,
  where,
} from "firebase/firestore";

import ListSubheader from "@mui/material/ListSubheader";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import { alpha } from "@mui/material/styles";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

// 1ページあたり件数
const PAGE_SIZE = 20;
// プロフィール「最近の投稿」：1ページ件数
const RECENT_PAGE_SIZE = 10;

// フィード表示の高さ（要望：最大200px）
// const FEED_MAX_HEIGHT = 200;
// フィード表示の高さ（復活）
const FEED_HEIGHT = { xs: 500, sm: 600, md: 700 };

// 47都道府県（固定）
const PREFECTURES = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
  "海外（アメリカ）",
  "海外（その他）",
];

const PREF_GROUPS = [
  { label: "全国", items: ["全地球の言づてを表示"] }, // ★追加（最上段に置く）

  { label: "北海道", items: ["北海道"] },
  {
    label: "東北",
    items: ["青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県"],
  },
  {
    label: "関東",
    items: [
      "茨城県",
      "栃木県",
      "群馬県",
      "埼玉県",
      "千葉県",
      "東京都",
      "神奈川県",
    ],
  },
  { label: "甲信越", items: ["新潟県", "山梨県", "長野県"] },
  { label: "北陸", items: ["富山県", "石川県", "福井県"] },
  { label: "東海", items: ["岐阜県", "静岡県", "愛知県", "三重県"] },
  {
    label: "近畿",
    items: ["滋賀県", "京都府", "大阪府", "兵庫県", "奈良県", "和歌山県"],
  },
  { label: "中国", items: ["鳥取県", "島根県", "岡山県", "広島県", "山口県"] },
  { label: "四国", items: ["徳島県", "香川県", "愛媛県", "高知県"] },
  {
    label: "九州・沖縄",
    items: [
      "福岡県",
      "佐賀県",
      "長崎県",
      "熊本県",
      "大分県",
      "宮崎県",
      "鹿児島県",
      "沖縄県",
    ],
  },
  { label: "海外", items: ["海外（アメリカ）", "海外（その他）"] },
];

const POST_RULES_TEXT =
  "【Cotozute 投稿ルール】\n" +
  "Cotozuteは、自分の内側を見つめるための記録であり、\n" +
  "同じ音を聴いているメンバー同士が、\n" +
  "ゆるやかにつながるための場です。\n\n" +
  "すべてのメンバーが心地よく利用できるよう、\n" +
  "以下のルールをご確認のうえ、ご利用ください。\n\n" +
  "・個人情報（氏名／住所／連絡先／口座情報／位置情報など）は投稿しないでください\n" +
  "・誹謗中傷、差別的な表現、脅迫行為は禁止します\n" +
  "・過度に露骨な性的表現や暴力的な表現は禁止します\n" +
  "・他者の文章や作品などの無断転載、著作権を侵害する投稿は禁止します\n" +
  "・宣伝、勧誘、営業行為、宗教・政治活動を目的とした投稿は禁止します\n\n" +
  "また、以下についてあらかじめご了承ください。\n" +
  "・ルールに違反する投稿は、予告なく非表示または削除される場合があります\n" +
  "・投稿内容は、運営の判断により保存・調整・削除される場合があります\n\n";

const hslToRgb01 = ({ h, s, l }) => {
  const S = clamp01(s / 100);
  const L = clamp01(l / 100);

  const C = (1 - Math.abs(2 * L - 1)) * S;
  const Hp = (((h % 360) + 360) % 360) / 60;
  const X = C * (1 - Math.abs((Hp % 2) - 1));

  let r1 = 0,
    g1 = 0,
    b1 = 0;
  if (0 <= Hp && Hp < 1) [r1, g1, b1] = [C, X, 0];
  else if (1 <= Hp && Hp < 2) [r1, g1, b1] = [X, C, 0];
  else if (2 <= Hp && Hp < 3) [r1, g1, b1] = [0, C, X];
  else if (3 <= Hp && Hp < 4) [r1, g1, b1] = [0, X, C];
  else if (4 <= Hp && Hp < 5) [r1, g1, b1] = [X, 0, C];
  else if (5 <= Hp && Hp < 6) [r1, g1, b1] = [C, 0, X];

  const m = L - C / 2;
  return { r: r1 + m, g: g1 + m, b: b1 + m };
};

const clamp01 = (v) => Math.max(0, Math.min(1, v));

// rgb(0..1) → hsl(h:0..360, s/l:0..100)
const rgb01ToHsl = ({ r, g, b }) => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));

    switch (max) {
      case r:
        h = ((g - b) / d) % 6;
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return {
    h,
    s: s * 100,
    l: l * 100,
  };
};

const hexToRgb01 = (hex) => {
  const h = (hex || "").replace("#", "").trim();
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  if (full.length !== 6) return { r: 0.35, g: 0.21, b: 1 }; // fallback
  const n = parseInt(full, 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  return { r, g, b };
};

const rgb01ToHex = ({ r, g, b }) => {
  const to255 = (x) => Math.round(clamp01(x) * 255);
  const toHex = (x) => to255(x).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// accent を “種” にして、グループごとに Hue を回して統一パレットを作る
const makeGroupColorFromAccent = (accentHex, groupIndex) => {
  const { r, g, b } = hexToRgb01(accentHex);
  const hsl = rgb01ToHsl({ r, g, b }); // ←ここを置き換え

  const hueStep = 22; // ←ここで色の散らし具合を調整（18〜28くらいが気持ちいい）
  const hue = (hsl.h + groupIndex * hueStep) % 360;

  // 彩度と明度は “見える” 側に寄せる（薄すぎ防止）
  const sat = Math.max(62, hsl.s); // 62%以上
  const lig = Math.min(52, Math.max(38, hsl.l)); // 38〜52%に収める

  const rgb01 = hslToRgb01({ h: hue, s: sat, l: lig });
  return rgb01ToHex(rgb01);
};

const renderPrefMenuItems = (getCountForPref, { accent, selectedPref }) =>
  PREF_GROUPS.flatMap((g) => {
    const isTop = g.label === "全国";

    return [
      <ListSubheader
        key={`sub-${g.label}`}
        disableSticky
        sx={{
          bgcolor: "transparent",
          py: 0.8,
          fontWeight: 950,
          fontSize: 11,
          letterSpacing: "0.14em",
          color: isTop ? alpha(accent, 0.9) : "rgba(0,0,0,0.45)",
        }}
      >
        {g.label}
      </ListSubheader>,

      ...g.items.map((pref) => {
        const n = Number(getCountForPref(pref) || 0);
        const isSelected = pref === selectedPref;
        const isZero = n <= 0;

        return (
          <MenuItem
            key={pref}
            value={pref}
            sx={{
              mx: 1,
              my: 0.25,
              borderRadius: 2,
              py: 1.0,
              opacity: isZero ? 0.5 : 1,
              bgcolor: isSelected ? alpha(accent, 0.1) : "transparent",
              "&:hover": {
                bgcolor: isSelected ? alpha(accent, 0.14) : "rgba(0,0,0,0.04)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              {/* 左の小ドット：選択中だけ強く */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: 99,
                  mr: 1.2,
                  bgcolor: isSelected ? accent : "rgba(0,0,0,0.18)",
                  boxShadow: isSelected
                    ? `0 0 0 3px ${alpha(accent, 0.18)}`
                    : "none",
                  flex: "0 0 auto",
                }}
              />

              <Typography
                sx={{
                  fontWeight: isSelected ? 950 : 800,
                  color: isSelected ? alpha(accent, 0.95) : "rgba(0,0,0,0.82)",
                }}
              >
                {pref}
              </Typography>

              <Box sx={{ flex: 1 }} />

              {/* 件数バッジ：視認性上がる */}
              <Box
                sx={{
                  px: 1.1,
                  py: 0.35,
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 950,
                  color: isSelected ? alpha(accent, 0.95) : "rgba(0,0,0,0.55)",
                  bgcolor: isSelected
                    ? alpha(accent, 0.12)
                    : "rgba(0,0,0,0.05)",
                  minWidth: 54,
                  textAlign: "center",
                }}
              >
                {n}件
              </Box>
            </Box>
          </MenuItem>
        );
      }),
    ];
  });

// ─────────────────────────────────────────────
// URL 抽出・リンク化
// ─────────────────────────────────────────────
const URL_REGEX = /(https?:\/\/[^\s　]+)/i;

function extractFirstUrl(text) {
  const match = text.match(URL_REGEX);
  return match ? match[0] : null;
}

function renderTextWithLinks(text) {
  if (!text) return null;
  const parts = text.split(URL_REGEX);

  return parts.map((part, idx) => {
    if (URL_REGEX.test(part)) {
      return (
        <MuiLink
          key={idx}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          sx={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            maxWidth: "100%",
            display: "inline",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </MuiLink>
      );
    }
    return (
      <span key={idx} style={{ whiteSpace: "pre-wrap" }}>
        {part}
      </span>
    );
  });
}

/* ==========================================================
      ⏱ 日付フォーマット / URLヘルパー
   ========================================================== */
function formatTimestamp(ts) {
  if (!ts) return "";
  let d;
  if (typeof ts.toDate === "function") d = ts.toDate();
  else if (ts instanceof Date) d = ts;
  else if (typeof ts === "string") d = new Date(ts);
  else return "";

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "たった今";
  if (diffMin < 60) return `${diffMin}分前`;
  if (diffHour < 24) return `${diffHour}時間前`;
  if (diffDay < 7) return `${diffDay}日前`;

  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function safeHostnameFromUrl(url) {
  try {
    const u = new URL(url);
    return u.hostname;
  } catch {
    return url;
  }
}

/* ==========================================================
    📱 メインのタイムライン（画像ゼロ版）
   ========================================================== */
export default function CommunityFeedNoImage() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";

  const { user } = useAuth();
  const { buildDateKey: buildDayKey } = useDayBoundary();
  const getTodayKeyJST = () => buildDayKey(new Date());

  const [userProfile, setUserProfile] = useState(null);

  // 投稿一覧
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // 投稿フォーム
  const [postText, setPostText] = useState("");
  const [submittingPost, setSubmittingPost] = useState(false);

  // 詳細ダイアログ
  const [detailPost, setDetailPost] = useState(null);

  // Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // ページング
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  // プロフィールダイアログ
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // ★今日キー（JST）
  //   const todayKey = useMemo(() => getTodayKeyJST(), []);

  // ★prefecture フィルタ
  const [selectedPref, setSelectedPref] = useState(""); // 初期は userProfile 取得後にセット

  // ★今日のprefecture件数（1doc）
  const [todayCounts, setTodayCounts] = useState({}); // { "東京都": 12, ... }
  const [todayTotal, setTodayTotal] = useState(0);

  // 規約同意ダイアログ
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [ruleAgreeChecked, setRuleAgreeChecked] = useState(false);
  const [ruleSubmitting, setRuleSubmitting] = useState(false);

  const REFRESH_COOLDOWN_MS = 3000;

  const refreshLockUntilRef = useRef(0);
  const [refreshCooling, setRefreshCooling] = useState(false);

  // ★強制リロード用（keyで再マウントさせる）
  const [refreshKey, setRefreshKey] = useState(0);

  const [todayKey, setTodayKey] = useState(getTodayKeyJST());
  useEffect(() => {
    const interval = setInterval(() => {
      const nextKey = getTodayKeyJST();
      setTodayKey((prev) => (prev !== nextKey ? nextKey : prev));
    }, 60 * 1000); // 1分に1回で十分

    return () => clearInterval(interval);
  }, []);

  const postRows = useMemo(() => {
    const len = (postText || "").length;
    // 22文字で1行増えるイメージ。上限は6行くらいで止める。
    const rows = 2 + Math.floor(len / 22);
    return Math.max(2, Math.min(6, rows));
  }, [postText]);

  // 0〜N のグループindexで、同系色を少しずつ変える
  const getGroupBg = (groupIndex) => {
    // 透明度と明るさをずらす（薄い→ちょい濃い の階調）
    const a1 = 0.06 + groupIndex * 0.01; // 左側
    const a2 = 0.1 + groupIndex * 0.012; // 右側

    return `linear-gradient(90deg, ${alpha(accent, a1)} 0%, ${alpha(
      accent,
      a2
    )} 100%)`;
  };

  const getGroupHeaderColor = (groupIndex) =>
    alpha(accent, 0.65 + groupIndex * 0.02);

  const handleRefresh = async () => {
    const now = Date.now();
    if (now < refreshLockUntilRef.current) return;
  
    refreshLockUntilRef.current = now + REFRESH_COOLDOWN_MS;
  
    setRefreshCooling(true);
    setTimeout(() => setRefreshCooling(false), REFRESH_COOLDOWN_MS);
  
    await fetchInitial();
    setSnackbarMessage("更新しました");
  };
  

  // users/{uid} から自分のプロフィール
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      setSelectedPref(""); // ログアウト時は空に戻す
      return;
    }
    let cancelled = false;

    const loadProfile = async () => {
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        if (cancelled) return;

        if (snap.exists()) {
          const prof = { id: user.uid, ...snap.data() };
          setUserProfile(prof);

          // ★デフォルト：自分のprefecture（無ければ "東京都"）
          const pref = prof.prefecture || prof.pref || prof.region || "";
          setSelectedPref((prev) => prev || pref || "東京都");
        } else {
          setUserProfile(null);
          setSelectedPref((prev) => prev || "東京都");
        }
      } catch (e) {
        console.error("Failed to load user profile:", e);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // ★今日の集計（1doc）
  useEffect(() => {
    if (!user) {
      setTodayCounts({});
      setTodayTotal(0);
      return;
    }

    const aggRef = doc(db, "mmPostDailyAggregates", todayKey);
    const unsub = onSnapshot(
      aggRef,
      (snap) => {
        if (!snap.exists()) {
          setTodayCounts({});
          setTodayTotal(0);
          return;
        }
        const data = snap.data() || {};
        setTodayCounts(data.prefectureCounts || {});
        setTodayTotal(Number(data.totalCount || 0));
      },
      (err) => {
        console.error("Failed to load daily aggregates:", err);
        setTodayCounts({});
        setTodayTotal(0);
      }
    );

    return () => unsub();
  }, [user, todayKey]);

  // ★今日 + prefecture のクエリを作る
  const buildPostsQuery = (lastDoc = null) => {
    const postsRef = collection(db, "mmPosts");

    const constraints = [
      where("dateKey", "==", todayKey),

      // ★全国の時は prefecture フィルタしない
      ...(selectedPref === "全地球の言づてを表示"
        ? []
        : [where("prefecture", "==", selectedPref)]),

      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE),
    ];

    if (lastDoc) {
      // limitの直前に startAfter を差し込む（あなたの元のやり方に合わせる）
      constraints.splice(constraints.length - 1, 0, startAfter(lastDoc));
    }

    return query(postsRef, ...constraints);
  };

  const fetchInitial = React.useCallback(async () => {
    if (!user || !selectedPref) {
      setPosts([]);
      setLastVisible(null);
      setHasMore(false);
      setLoading(false);
      return;
    }
  
    setLoading(true);
    setPosts([]);
    setLastVisible(null);
    setHasMore(false);
  
    try {
      const q = buildPostsQuery(null);
      const snap = await getDocs(q);
  
      const list = snap.docs.map((d) => {
        const data = d.data();
        const likes = data.likes || {};
        const isLiked = user ? !!likes[user.uid] : false;
        return { id: d.id, ...data, isLiked };
      });
  
      setPosts(list);
  
      const lastDoc = snap.docs[snap.docs.length - 1] || null;
      setLastVisible(lastDoc);
      setHasMore(snap.size === PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load posts:", err);
    } finally {
      setLoading(false);
    }
  }, [user, selectedPref, todayKey]);
  

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  // ★初回取得（または prefecture 切替時に全リセットして再取得）
  //   useEffect(() => {
  //     let cancelled = false;

  //     const fetchInitial = async () => {
  //       // user と selectedPref が揃ってから動く
  //       if (!user || !selectedPref) {
  //         setPosts([]);
  //         setLastVisible(null);
  //         setHasMore(false);
  //         setLoading(false);
  //         return;
  //       }

  //       setLoading(true);
  //       setPosts([]);
  //       setLastVisible(null);
  //       setHasMore(false);

  //       try {
  //         const q = buildPostsQuery(null);
  //         const snap = await getDocs(q);
  //         if (cancelled) return;

  //         const list = snap.docs.map((d) => {
  //           const data = d.data();
  //           const likes = data.likes || {};
  //           const isLiked = user ? !!likes[user.uid] : false;
  //           return { id: d.id, ...data, isLiked };
  //         });

  //         setPosts(list);

  //         const lastDoc = snap.docs[snap.docs.length - 1] || null;
  //         setLastVisible(lastDoc);
  //         setHasMore(snap.size === PAGE_SIZE);
  //       } catch (err) {
  //         if (!cancelled) console.error("Failed to load posts:", err);
  //       } finally {
  //         if (!cancelled) setLoading(false);
  //       }
  //     };

  //     fetchInitial();
  //     return () => {
  //       cancelled = true;
  //     };
  //   }, [user, selectedPref, todayKey]);

  const loadMore = async () => {
    if (!user || !selectedPref) return;
    if (!lastVisible || !hasMore || loadingMore) return;

    setLoadingMore(true);
    try {
      const q = buildPostsQuery(lastVisible);
      const snap = await getDocs(q);

      const list = snap.docs.map((d) => {
        const data = d.data();
        const likes = data.likes || {};
        const isLiked = user ? !!likes[user.uid] : false;
        return { id: d.id, ...data, isLiked };
      });

      setPosts((prev) => [...prev, ...list]);

      const last = snap.docs[snap.docs.length - 1] || null;
      setLastVisible(last);
      setHasMore(snap.size === PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load more posts:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  // 無限スクロール用 IntersectionObserver
  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) return;
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) loadMore();
      },
      { threshold: 0.9 }
    );

    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, sentinelRef.current, lastVisible, selectedPref]);

  // プロフィールを開く（一覧などから）
  const handleOpenProfile = async (userId, fallback = {}) => {
    if (!userId) return;

    setProfileUser((prev) => ({
      id: userId,
      userName: fallback.userName ?? prev?.userName,
      avatar: fallback.avatar ?? prev?.avatar,
    }));
    setProfileOpen(true);
    setProfileLoading(true);

    try {
      const ref = doc(db, "users", userId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setProfileUser({
          id: userId,
          ...data,
          userName:
            data.name ||
            data.registrationName ||
            fallback.userName ||
            data.userName,
          avatar: data.avatar || fallback.avatar,
        });
      } else {
        setProfileUser((prev) => ({
          ...prev,
          userName:
            fallback.userName ??
            prev?.userName ??
            "プロフィール情報が登録されていません",
        }));
      }
    } catch (e) {
      console.error("Failed to load profile:", e);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleOpenProfileFromDetail = (userId, fallback = {}) => {
    setDetailPost(null);
    setTimeout(() => handleOpenProfile(userId, fallback), 0);
  };

  const handleCloseProfile = () => setProfileOpen(false);

  const postTextCount = postText.length;
  const postTextOver = postTextCount > 88;

  const handleCreatePost = async () => {
    if (!user) return;
    if (!postText.trim()) return;
    if (postTextOver) return;

    // ★ 規約同意チェックは「ここでだけ」
    const agreed = Boolean(userProfile?.postRuleAgreed);

    if (!agreed) {
      setRuleAgreeChecked(false);
      setRuleDialogOpen(true);
      return;
    }

    // 同意済みなら通常投稿
    await actuallyCreatePost();
  };

  // 既存の中身はそのまま
  const actuallyCreatePost = async () => {
    setSubmittingPost(true);

    try {
      const firstUrl = extractFirstUrl(postText);

      const postsRef = collection(db, "mmPosts");

      const displayName =
        userProfile?.name ||
        userProfile?.registrationName ||
        user?.displayName ||
        "名無し";

      const avatarUrl = userProfile?.avatar || user?.photoURL || "";

      const myPref =
        userProfile?.prefecture ||
        userProfile?.pref ||
        userProfile?.region ||
        "";

      await addDoc(postsRef, {
        userId: user.uid,
        userName: displayName,
        userAvatar: avatarUrl,
        text: postText.trim(),
        linkUrl: firstUrl || null,
        linkMeta: null,
        prefecture: myPref || selectedPref || "",
        dateKey: todayKey,
        likeCount: 0,
        likes: {},
        commentCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setPostText("");
      setSnackbarMessage("投稿が完了しました");
      // ★追加：投稿後に一覧を取り直す
      await fetchInitial();
    } catch (err) {
      console.error("Failed to create post:", err);
    } finally {
      setSubmittingPost(false);
    }
  };

  // いいねトグル
  const handleTogglePostLike = async (postId) => {
    if (!user) return;
    const current = posts.find((p) => p.id === postId);
    if (!current) return;

    const newIsLiked = !current.isLiked;
    const delta = newIsLiked ? 1 : -1;

    // ローカル即時反映（一覧）
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, isLiked: newIsLiked, likeCount: (p.likeCount || 0) + delta }
          : p
      )
    );

    // ★追加：詳細ダイアログ表示中の post も即時反映
    setDetailPost((prev) =>
      prev && prev.id === postId
        ? {
            ...prev,
            isLiked: newIsLiked,
            likeCount: (prev.likeCount || 0) + delta,
          }
        : prev
    );

    try {
      const postRef = doc(db, "mmPosts", postId);
      const field = `likes.${user.uid}`;

      if (newIsLiked) {
        await updateDoc(postRef, { [field]: true, likeCount: increment(1) });
      } else {
        await updateDoc(postRef, {
          [field]: deleteField(),
          likeCount: increment(-1),
        });
      }
    } catch (err) {
      console.error("Failed to toggle like:", err);

      // 失敗時は巻き戻し（一覧）
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                isLiked: !newIsLiked,
                likeCount: (p.likeCount || 0) - delta,
              }
            : p
        )
      );

      // ★追加：詳細も巻き戻し
      setDetailPost((prev) =>
        prev && prev.id === postId
          ? {
              ...prev,
              isLiked: !newIsLiked,
              likeCount: (prev.likeCount || 0) - delta,
            }
          : prev
      );
    }
  };

  const handleOpenDetail = (post) => setDetailPost(post);
  const handleCloseDetail = () => setDetailPost(null);

  // Select用：県ごとの今日件数
  const getCountForPref = (pref) => {
    if (pref === "全地球の言づてを表示") return Number(todayTotal || 0);
    return Number(todayCounts?.[pref] || 0);
  };

  return (
    <Box
    //   key={refreshKey}
      sx={{
        height: FEED_HEIGHT,
        maxHeight: FEED_HEIGHT,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: surface,
        color: textBase,
        position: "relative",
        border: "1px solid rgba(0,0,0,0.06)",
        borderRadius: 2,
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: surface,
          borderBottom: `1px solid ${theme.bottomBorder || "rgba(0,0,0,0.06)"}`,
          px: 2,
          py: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Button
            onClick={handleRefresh}
            disabled={refreshCooling}
            startIcon={<AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />}
            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 2.2,
              py: 0.65,
              fontSize: 13,
              fontWeight: 900,
              letterSpacing: "0.08em",
              bgcolor: theme.railActive || accent,
              color: "#fff",
              boxShadow: "0 10px 24px rgba(20,16,40,0.28)",
              "&:hover": {
                bgcolor: theme.railActive || accent,
                boxShadow: "0 14px 30px rgba(20,16,40,0.34)",
                transform: "translateY(-1px)",
              },
              "& .MuiButton-startIcon": { mr: 0.8 },
              "& .MuiButton-endIcon": { ml: 0.4 },
              transition: "all .18s ease-out",
              // 押せる感を明確に
              cursor: "pointer",
            }}
          >
            {/* メイン名 */}
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Box component="span">Cotozute</Box>

              {/* 地域のサブ表示：これが「切り替わってる感」を強くする */}
              {/* <Box
                component="span"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.02em",
                  opacity: 0.92,
                  fontSize: 11,
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.16)",
                }}
              >
                {selectedPref || "—"}
              </Box> */}
            </Box>
          </Button>

          <Box sx={{ flex: 1 }} />

          {/* ★今日 + 県の表示（軽いステータス） */}
          <Chip
            size="small"
            label={`${todayKey}`}
            sx={{ bgcolor: "rgba(0,0,0,0.04)", fontWeight: 700 }}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box sx={{ p: 0, maxWidth: 720, mx: "auto" }}>
          {/* 投稿フォーム */}
          <Card
            elevation={0}
            sx={{
              mb: 2,
              borderRadius: 3,
              //   border: "1px solid rgba(0,0,0,0.08)",
              overflow: "hidden",
              bgcolor: surface,
            }}
          >
            <CardContent sx={{ pb: 1.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Avatar
                  src={userProfile?.avatar || user?.photoURL || ""}
                  sx={{ width: 40, height: 40 }}
                >
                  {(
                    userProfile?.name ||
                    userProfile?.registrationName ||
                    user?.displayName ||
                    "？"
                  ).charAt(0)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* セリフ（抜き出し）風の入力枠 */}
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 3,
                      border: "1px solid rgba(0,0,0,0.10)",
                      bgcolor: theme.appBg || "rgba(0,0,0,0.02)",
                      px: 1.5,
                      pt: 1.4,
                      pb: 1.1,
                    }}
                  >
                    {/* <FormatQuoteRoundedIcon
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 10,
                        fontSize: 18,
                        opacity: 0.35,
                      }}
                    /> */}

                    <TextField
                      multiline
                      fullWidth
                      minRows={postRows}
                      maxRows={8}
                      placeholder="いまの感覚を言の葉でみんなに伝えよう（88文字まで）"
                      value={postText}
                      onChange={(e) => setPostText(e.target.value)}
                      inputProps={{ maxLength: 200 }}
                      sx={{
                        mt: 0.2,

                        // ★ ここが本丸
                        "& .MuiOutlinedInput-root": {
                          padding: 0,

                          "& fieldset": {
                            border: "none", // 通常
                          },
                          "&:hover fieldset": {
                            border: "none", // hover
                          },
                          "&.Mui-focused fieldset": {
                            border: "none", // focus（青線の正体）
                          },
                        },
                      }}
                    />

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{ mt: 0.7 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: postTextOver ? "error.main" : "text.secondary",
                        }}
                      >
                        {postTextCount}/88
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<SendRoundedIcon />}
                        disabled={
                          submittingPost || !postText.trim() || postTextOver
                        }
                        onClick={handleCreatePost}
                        sx={{
                          borderRadius: 99,
                          px: 2,
                          fontWeight: 900,
                          textTransform: "none",
                        }}
                      >
                        投稿
                      </Button>
                    </Stack>
                  </Box>

                  {/* ✅ 投稿セクションの下に「表示する都道府県」 */}
                  <Box sx={{ mt: 1.2 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="pref-select-label">
                        表示する範囲
                      </InputLabel>
                      <Select
                        labelId="pref-select-label"
                        value={selectedPref || ""}
                        label="表示する都道府県"
                        onChange={(e) =>
                          setSelectedPref(String(e.target.value || ""))
                        }
                        sx={{
                          "& .MuiSelect-select": {
                            fontWeight: 900,
                            color: textBase,
                            py: 1.15,
                            display: "flex",
                            alignItems: "center",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0,0,0,0.10)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0,0,0,0.18)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(accent, 0.55),
                          },
                          "& .MuiSelect-icon": {
                            color: "rgba(0,0,0,0.35)",
                          },
                          borderRadius: 3,
                          bgcolor: "rgba(0,0,0,0.02)",
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: 3,
                              mt: 1,
                              boxShadow: "0 14px 40px rgba(15,10,40,0.22)",
                              // ✅ メニューの面はsurface寄せでAppShellに馴染ませる
                              bgcolor: theme.surfaceBg || "#fff",
                              border: `1px solid ${alpha(accent, 0.12)}`,
                            },
                          },
                        }}
                      >
                        {renderPrefMenuItems(getCountForPref, {
                          accent,
                          selectedPref,
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* 投稿一覧 */}
          {loading ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : posts.length === 0 ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                今日はまだ投稿がありません。最初の一言を投稿してみましょう。
              </Typography>
            </Box>
          ) : (
            <>
              <Stack spacing={0}>
                {posts.map((post) => (
                  <PostCardNoImage
                    key={post.id}
                    post={post}
                    currentUser={user}
                    theme={theme}
                    onToggleLike={() => handleTogglePostLike(post.id)}
                    onOpenDetail={() => handleOpenDetail(post)}
                    onDeleted={() => setSnackbarMessage("投稿を削除しました")}
                    onOpenProfile={(userId, fallback) =>
                      handleOpenProfile(userId, fallback)
                    }
                  />
                ))}
              </Stack>

              {/* 読み込み用 sentinel */}
              <Box ref={sentinelRef} sx={{ height: hasMore ? 32 : 0 }} />

              {loadingMore && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!hasMore && !loadingMore && posts.length > 0 && (
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    すべて読み込みました
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* 投稿中オーバーレイ */}
      {submittingPost && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            bgcolor: "rgba(0,0,0,0.45)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#fff" }} />
          <Typography
            variant="body1"
            sx={{ mt: 2, color: "#fff", fontWeight: 700 }}
          >
            投稿しています…
          </Typography>
        </Box>
      )}

      {/* 詳細ダイアログ */}
      {detailPost && (
        <PostDetailDialogNoImage
          open={Boolean(detailPost)}
          onClose={handleCloseDetail}
          post={detailPost}
          currentUser={user}
          theme={theme}
          userProfile={userProfile}
          onToggleLike={() => handleTogglePostLike(detailPost.id)}
          onCommentDeleted={() => setSnackbarMessage("コメントを削除しました")}
          onOpenProfile={(userId, fallback) =>
            handleOpenProfileFromDetail(userId, fallback)
          }
        />
      )}

      {/* プロフィールドロワー */}
      <ProfileDialogNoImage
        open={profileOpen}
        onClose={handleCloseProfile}
        user={profileUser}
        loading={profileLoading}
        theme={theme}
        currentUser={user}
        currentUserProfile={userProfile}
      />

      <Dialog
        open={ruleDialogOpen}
        fullWidth
        maxWidth="sm"
        onClose={(event, reason) => {
          if (ruleSubmitting) return;

          // 背景クリック/ESCでは閉じない
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;

          setRuleDialogOpen(false);
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>投稿前の確認</DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              lineHeight: 1.8,
              mb: 2,
            }}
          >
            {POST_RULES_TEXT}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={ruleAgreeChecked}
                onChange={(e) => setRuleAgreeChecked(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">上記内容に同意します</Typography>
            }
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              if (ruleSubmitting) return;
              setRuleDialogOpen(false);
            }}
            disabled={ruleSubmitting}
          >
            キャンセル
          </Button>

          <Button
            variant="contained"
            disabled={!ruleAgreeChecked || ruleSubmitting}
            onClick={async () => {
              if (!user) return;

              setRuleSubmitting(true);
              try {
                await updateDoc(doc(db, "users", user.uid), {
                  postRuleAgreed: true,
                  postRuleAgreedAt: serverTimestamp(),
                });

                // ★ local state も即更新
                setUserProfile((prev) =>
                  prev ? { ...prev, postRuleAgreed: true } : prev
                );

                setRuleDialogOpen(false);
                await actuallyCreatePost();
              } catch (e) {
                console.error("Failed to save rule agreement:", e);
              } finally {
                setRuleSubmitting(false);
              }
            }}
          >
            同意して投稿する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbarMessage("")}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ==========================================================
    🧩 投稿カード（画像ゼロ版）
   ========================================================== */
function PostCardNoImage({
  post,
  currentUser,
  theme,
  onToggleLike,
  onOpenDetail,
  onDeleted,
  onOpenProfile,
}) {
  const surface = theme.surfaceBg || "#FFFFFF";
  const accent = theme.railActive || "#5A35FF";

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isMine = currentUser && post.userId === currentUser.uid;

  const stopEvent = (e) => {
    if (!e) return;
    e.stopPropagation();
    e.preventDefault?.();
    if (e.nativeEvent?.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  const isModalBlocking = Boolean(menuAnchor) || deleteDialogOpen || deleting;

  const handleOpenMenu = (e) => {
    stopEvent(e);
    setMenuAnchor(e.currentTarget);
  };
  const handleCloseMenu = (e) => {
    if (e) stopEvent(e);
    setMenuAnchor(null);
  };

  const handleClickDeleteMenu = (e) => {
    if (e) stopEvent(e);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleCancelDelete = (e) => {
    if (e) stopEvent(e);
    if (deleting) return;
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async (e) => {
    if (e) stopEvent(e);
    if (!currentUser || !isMine) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, "mmPosts", post.id));
    } catch (err) {
      console.error("Failed to delete post:", err);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setMenuAnchor(null);
      if (onDeleted) onDeleted();
    }
  };

  const handleCardClick = (e) => {
    if (isModalBlocking) {
      stopEvent(e);
      return;
    }
    if (onOpenDetail) onOpenDetail();
  };

  const handleLikeClick = (e) => {
    stopEvent(e);
    if (onToggleLike) onToggleLike();
  };

  const handleCommentIconClick = (e) => {
    stopEvent(e);
    if (onOpenDetail) onOpenDetail();
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0,
        borderBottom: "1px solid rgba(0,0,0,0.05)",
        bgcolor: surface,
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
      }}
      onClick={handleCardClick}
    >
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 160,
            borderRadius: 2,
            boxShadow: "0 10px 30px rgba(15,10,40,0.25)",
          },
        }}
      >
        <MenuItem onClick={handleClickDeleteMenu}>
          <ListItemIcon>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </ListItemIcon>
          投稿を削除
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        fullWidth
        maxWidth="xs"
        onClose={(e) => handleCancelDelete(e)}
        slotProps={{
          backdrop: { onClick: (e) => stopEvent(e) },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberRoundedIcon
            sx={{ color: "warning.main", fontSize: 24 }}
          />
          この投稿を削除しますか？
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            この操作は取り消せません。消去した投稿はもとに戻せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "削除中..." : "削除する"}
          </Button>
        </DialogActions>
      </Dialog>

      <CardHeader
        avatar={
          <Avatar
            src={post.userAvatar || ""}
            sx={{ px: 0, cursor: "pointer" }}
            onClick={(e) => {
              stopEvent(e);
              if (onOpenProfile) {
                onOpenProfile(post.userId, {
                  userName: post.userName,
                  avatar: post.userAvatar,
                });
              }
            }}
          >
            {(post.userName || "？").charAt(0)}
          </Avatar>
        }
        title={
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.1, mb: 0.2 }}
          >
            {post.userName || "名無し"}
          </Typography>
        }
        subheader={
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {formatTimestamp(post.createdAt)}
          </Typography>
        }
        action={
          isMine && (
            <IconButton
              size="small"
              onClick={handleOpenMenu}
              aria-label="post menu"
            >
              <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
          )
        }
        sx={{ py: 0 }}
      />

      <CardContent sx={{ pt: 0, pb: 1.2 }}>
        {post.text && (
          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              maxWidth: "100%",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {renderTextWithLinks(post.text)}
          </Typography>
        )}

        {post.linkUrl && (
          <Box
            component={MuiLink}
            href={post.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            onClick={(e) => stopEvent(e)}
            sx={{
              display: "block",
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.08)",
              p: 1.2,
              mb: 1.2,
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {safeHostnameFromUrl(post.linkUrl)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, mt: 0.3 }}>
              {post.linkUrl}
            </Typography>
          </Box>
        )}

        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={handleLikeClick}
            disabled={!currentUser}
          >
            {post.isLiked ? (
              <FavoriteRoundedIcon sx={{ color: accent }} fontSize="small" />
            ) : (
              <FavoriteBorderRoundedIcon fontSize="small" />
            )}
          </IconButton>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {post.likeCount || 0}
          </Typography>

          <IconButton
            size="small"
            onClick={handleCommentIconClick}
            sx={{ mr: 0.5 }}
          >
            <ChatBubbleOutlineRoundedIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2">{post.commentCount ?? 0}</Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

/* ==========================================================
    🧩 投稿詳細ダイアログ（画像ゼロ版）
   ========================================================== */
function PostDetailDialogNoImage({
  open,
  onClose,
  post,
  currentUser,
  theme,
  userProfile,
  onToggleLike,
  onCommentDeleted,
  onOpenProfile,
}) {
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const isXs = useMediaQuery("(max-width:600px)");

  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);

  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const commentTextCount = commentText.length;
  const commentTextOver = commentTextCount > 88;

  const [targetComment, setTargetComment] = useState(null);
  const [commentMenuAnchor, setCommentMenuAnchor] = useState(null);
  const [commentDeleteDialogOpen, setCommentDeleteDialogOpen] = useState(false);
  const [deletingComment, setDeletingComment] = useState(false);

  useEffect(() => {
    if (!open || !post?.id) return;

    setLoadingComments(true);

    const commentsRef = collection(db, "mmPosts", post.id, "comments");
    const q = query(commentsRef, orderBy("createdAt", "desc"), limit(100));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data();
          const likes = data.likes || {};
          const isLiked = currentUser ? !!likes[currentUser.uid] : false;
          return { id: d.id, ...data, isLiked };
        });
        setComments(list);
        setLoadingComments(false);
      },
      (err) => {
        console.error("Failed to load comments:", err);
        setLoadingComments(false);
      }
    );

    return () => unsub();
  }, [open, post?.id, currentUser?.uid]);

  const commentCount = comments.length;

  const handleCreateComment = async () => {
    if (!currentUser) return;
    if (!commentText.trim()) return;
    if (commentTextOver) return;

    setSubmittingComment(true);

    try {
      const commentsRef = collection(db, "mmPosts", post.id, "comments");

      const displayName =
        userProfile?.name ||
        userProfile?.registrationName ||
        currentUser.displayName ||
        "名無し";

      const avatarUrl = userProfile?.avatar || currentUser.photoURL || "";

      await addDoc(commentsRef, {
        userId: currentUser.uid,
        userName: displayName,
        userAvatar: avatarUrl,
        text: commentText.trim(),
        likeCount: 0,
        likes: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "mmPosts", post.id), {
        commentCount: increment(1),
      });

      setCommentText("");
    } catch (err) {
      console.error("Failed to create comment:", err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleToggleCommentLike = async (comment) => {
    if (!currentUser) return;

    try {
      const commentRef = doc(db, "mmPosts", post.id, "comments", comment.id);
      const field = `likes.${currentUser.uid}`;

      if (comment.isLiked) {
        await updateDoc(commentRef, {
          [field]: deleteField(),
          likeCount: increment(-1),
        });
      } else {
        await updateDoc(commentRef, {
          [field]: true,
          likeCount: increment(1),
        });
      }
    } catch (err) {
      console.error("Failed to toggle comment like:", err);
    }
  };

  const handleOpenCommentMenu = (e, comment) => {
    e.stopPropagation();
    setTargetComment(comment);
    setCommentMenuAnchor(e.currentTarget);
  };

  const handleCloseCommentMenu = () => setCommentMenuAnchor(null);

  const handleOpenCommentDeleteDialog = () => {
    setCommentDeleteDialogOpen(true);
    handleCloseCommentMenu();
  };

  const handleCancelCommentDelete = (e) => {
    if (e) e.stopPropagation();
    if (deletingComment) return;
    setCommentDeleteDialogOpen(false);
    setTargetComment(null);
  };

  const handleConfirmCommentDelete = async (e) => {
    if (e) e.stopPropagation();
    if (!currentUser || !targetComment) return;
    if (targetComment.userId !== currentUser.uid) return;

    setDeletingComment(true);
    try {
      await deleteDoc(
        doc(db, "mmPosts", post.id, "comments", targetComment.id)
      );
      await updateDoc(doc(db, "mmPosts", post.id), {
        commentCount: increment(-1),
      });

      setComments((prev) => prev.filter((c) => c.id !== targetComment.id));
      if (onCommentDeleted) onCommentDeleted();
    } catch (err) {
      console.error("Failed to delete comment:", err);
    } finally {
      setDeletingComment(false);
      setCommentDeleteDialogOpen(false);
      setTargetComment(null);
    }
  };

  const handleClose = () => {
    if (submittingComment) return;
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isXs}
      fullWidth={!isXs}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isXs ? "100vw" : 600,
          maxWidth: 600,
          height: isXs ? "100dvh" : "auto",
          m: 0,
          borderRadius: isXs ? 0 : 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 900,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          px: 1,
          py: 1.1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="back"
          sx={{ ml: 0.5 }}
        >
          <KeyboardArrowLeftRoundedIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 900 }}>投稿の詳細</Typography>
        <Box sx={{ flex: 1 }} />
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Box
          sx={{ flex: 1, overflowY: "auto", px: isXs ? 0 : 2, pt: 2, pb: 2 }}
        >
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar
                src={post.userAvatar || ""}
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenProfile) {
                    onOpenProfile(post.userId, {
                      userName: post.userName,
                      avatar: post.userAvatar,
                    });
                  }
                }}
              >
                {(post.userName || "？").charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 0.3 }}
                >
                  {post.userName || "名無し"}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {formatTimestamp(post.createdAt)}
                </Typography>
              </Box>
            </Stack>

            {post.text && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  maxWidth: "100%",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {renderTextWithLinks(post.text)}
              </Typography>
            )}

            {post.linkUrl && (
              <Box
                component={MuiLink}
                href={post.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "block",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.08)",
                  mt: 1.5,
                  p: 1.2,
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {safeHostnameFromUrl(post.linkUrl)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, mt: 0.3 }}>
                  {post.linkUrl}
                </Typography>
              </Box>
            )}

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <IconButton
                size="small"
                onClick={onToggleLike}
                disabled={!currentUser}
              >
                {post.isLiked ? (
                  <FavoriteRoundedIcon
                    sx={{ color: accent }}
                    fontSize="small"
                  />
                ) : (
                  <FavoriteBorderRoundedIcon fontSize="small" />
                )}
              </IconButton>
              <Typography variant="body2" sx={{ mr: 2 }}>
                {post.likeCount || 0}
              </Typography>

              <ChatBubbleOutlineRoundedIcon
                fontSize="small"
                sx={{ opacity: 0.7, mr: 0.5 }}
              />
              <Typography variant="body2">{commentCount}</Typography>
            </Stack>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {loadingComments ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : comments.length === 0 ? (
            <Typography
              variant="caption"
              sx={{
                opacity: 0.7,
                display: "block",
                textAlign: "center",
                py: 1,
              }}
            >
              まだコメントはありません。
            </Typography>
          ) : (
            <Stack spacing={1.2} sx={{ mb: 1 }}>
              {comments.map((c) => {
                const isMineComment =
                  currentUser && c.userId === currentUser.uid;

                return (
                  <Stack
                    key={c.id}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Avatar
                      src={c.userAvatar || ""}
                      sx={{ width: 28, height: 28, mt: 0.2, cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onOpenProfile) {
                          onOpenProfile(c.userId, {
                            userName: c.userName,
                            avatar: c.userAvatar,
                          });
                        }
                      }}
                    >
                      {(c.userName || "？").charAt(0)}
                    </Avatar>

                    <Box
                      sx={{
                        flex: 1,
                        px: 1,
                        py: 0.6,
                        borderRadius: 2,
                        bgcolor: theme.appBg || "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Stack
                          direction="row"
                          alignItems="baseline"
                          spacing={0.5}
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700 }}
                          >
                            {c.userName || "名無し"}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.6 }}>
                            {formatTimestamp(c.createdAt)}
                          </Typography>
                        </Stack>

                        {isMineComment && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenCommentMenu(e, c)}
                          >
                            <MoreVertRoundedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {c.text && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.2,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {renderTextWithLinks(c.text)}
                        </Typography>
                      )}

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ mt: 0.3 }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleToggleCommentLike(c)}
                          disabled={!currentUser}
                        >
                          {c.isLiked ? (
                            <FavoriteRoundedIcon
                              sx={{ color: accent }}
                              fontSize="small"
                            />
                          ) : (
                            <FavoriteBorderRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                        <Typography variant="caption">
                          {c.likeCount || 0}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          )}

          <Menu
            anchorEl={commentMenuAnchor}
            open={Boolean(commentMenuAnchor)}
            onClose={handleCloseCommentMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                minWidth: 160,
                borderRadius: 2,
                boxShadow: "0 10px 30px rgba(15,10,40,0.25)",
              },
            }}
          >
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleOpenCommentDeleteDialog();
              }}
            >
              <ListItemIcon>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </ListItemIcon>
              コメントを削除
            </MenuItem>
          </Menu>

          <Dialog
            open={commentDeleteDialogOpen}
            onClose={handleCancelCommentDelete}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningAmberRoundedIcon
                sx={{ color: "warning.main", fontSize: 24 }}
              />
              このコメントを削除しますか？
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                この操作は取り消せません。消去したコメントはもとに戻せません。
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCancelCommentDelete}
                disabled={deletingComment}
              >
                キャンセル
              </Button>
              <Button
                onClick={handleConfirmCommentDelete}
                color="error"
                variant="contained"
                disabled={deletingComment}
              >
                {deletingComment ? "削除中..." : "削除する"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: surface,
            borderTop: "1px solid rgba(0,0,0,0.06)",
            px: 2,
            py: 1,
            zIndex: 1,
          }}
        >
          {currentUser && (
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar
                src={userProfile?.avatar || currentUser.photoURL || ""}
                sx={{ width: 28, height: 28, mt: 0.5 }}
              >
                {(
                  userProfile?.name ||
                  userProfile?.registrationName ||
                  currentUser.displayName ||
                  "？"
                ).charAt(0)}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <TextField
                  multiline
                  fullWidth
                  minRows={1}
                  maxRows={3}
                  placeholder="コメント（88文字まで）"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  inputProps={{ maxLength: 88 }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    bgcolor: "rgba(0,0,0,0.02)",
                    borderRadius: 2,
                    px: 1,
                    py: 0.3,
                  }}
                />

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ mt: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: commentTextOver ? "error.main" : "text.secondary",
                    }}
                  >
                    {commentTextCount}/88
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleCreateComment}
                    disabled={
                      submittingComment ||
                      !commentText.trim() ||
                      commentTextOver
                    }
                  >
                    <SendRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            </Stack>
          )}

          {submittingComment && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255,255,255,0.7)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                コメントを投稿しています…
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ==========================================================
// 🧩 プロフィールダイアログ（画像ゼロ版・右からスライド）
// ==========================================================
const ProfileTransition = React.forwardRef(function ProfileTransition(
  props,
  ref
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

function ProfileDialogNoImage({
  open,
  onClose,
  user,
  loading,
  theme,
  currentUser,
  currentUserProfile,
}) {
  const surface = theme.surfaceBg || "#FFFFFF";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";
  const isXs = useMediaQuery("(max-width:600px)");

  const displayName =
    user?.name ||
    user?.registrationName ||
    user?.userName ||
    "プロフィール未登録";
  const avatarUrl = user?.avatar || user?.userAvatar || "";
  const prefecture = user?.prefecture || user?.pref || user?.region || null;
  const bio =
    user?.bio || user?.profileMessage || user?.selfIntroduction || null;

  const [view, setView] = useState("profile");
  const [selectedPost, setSelectedPost] = useState(null);

  const [recentPosts, setRecentPosts] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentLoadingMore, setRecentLoadingMore] = useState(false);
  const [recentLastVisible, setRecentLastVisible] = useState(null);
  const [recentHasMore, setRecentHasMore] = useState(false);

  const profileScrollRef = useRef(null);
  const savedProfileScrollTopRef = useRef(0);
  const recentSentinelRef = useRef(null);

  const [postComments, setPostComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  const [postBusy, setPostBusy] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const [ddpText, setDdpText] = useState("");
  const [ddpDisplayLoading, setDdpDisplayLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setView("profile");
      setSelectedPost(null);
      setRecentPosts([]);
      setRecentLastVisible(null);
      setRecentHasMore(false);
      setRecentLoading(false);
      setRecentLoadingMore(false);
      setPostComments([]);
      setCommentText("");
      savedProfileScrollTopRef.current = 0;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!user?.id) return;

    if (!user?.ddpProfileEnabled) {
      setDdpText("");
      setDdpDisplayLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setDdpDisplayLoading(true);
      try {
        const colRef = collection(db, "users", user.id, "ddps");
        const snap = await getDocs(
          query(colRef, orderBy("updatedAt", "desc"), limit(1))
        );
        if (cancelled) return;

        if (!snap.empty) {
          const data = snap.docs[0].data();
          setDdpText(String(data.ddp || ""));
        } else {
          setDdpText("");
        }
      } catch (e) {
        console.error("Failed to load DDP for profile:", e);
        if (!cancelled) setDdpText("");
      } finally {
        if (!cancelled) setDdpDisplayLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, user?.id, user?.ddpProfileEnabled]);

  useEffect(() => {
    if (!open) return;

    setView("profile");
    setSelectedPost(null);

    setRecentPosts([]);
    setRecentLastVisible(null);
    setRecentHasMore(false);
    setRecentLoading(false);
    setRecentLoadingMore(false);

    setPostComments([]);
    setCommentText("");
    savedProfileScrollTopRef.current = 0;
  }, [open, user?.id]);

  const fetchRecentInitial = async () => {
    if (!open || !user?.id) return;
    if (recentLoading || recentLoadingMore) return;
    if (recentPosts.length > 0) return;

    setRecentLoading(true);
    try {
      const postsRef = collection(db, "mmPosts");
      const q = query(
        postsRef,
        where("userId", "==", user.id),
        orderBy("createdAt", "desc"),
        limit(RECENT_PAGE_SIZE)
      );

      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRecentPosts(list);

      const lastDoc = snap.docs[snap.docs.length - 1] || null;
      setRecentLastVisible(lastDoc);
      setRecentHasMore(snap.size === RECENT_PAGE_SIZE);
    } catch (e) {
      console.error("Failed to load recent posts:", e);
      setRecentPosts([]);
      setRecentLastVisible(null);
      setRecentHasMore(false);
    } finally {
      setRecentLoading(false);
    }
  };

  const loadMoreRecent = async () => {
    if (!open || !user?.id) return;
    if (!recentLastVisible || !recentHasMore || recentLoadingMore) return;

    setRecentLoadingMore(true);
    try {
      const postsRef = collection(db, "mmPosts");
      const q = query(
        postsRef,
        where("userId", "==", user.id),
        orderBy("createdAt", "desc"),
        startAfter(recentLastVisible),
        limit(RECENT_PAGE_SIZE)
      );

      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRecentPosts((prev) => [...prev, ...list]);

      const lastDoc = snap.docs[snap.docs.length - 1] || null;
      setRecentLastVisible(lastDoc);
      setRecentHasMore(snap.size === RECENT_PAGE_SIZE);
    } catch (e) {
      console.error("Failed to load more recent posts:", e);
    } finally {
      setRecentLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!open || !user?.id) return;
    if (view !== "profile") return;
    fetchRecentInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id, view]);

  useEffect(() => {
    if (!open) return;
    if (view !== "profile") return;
    if (!recentHasMore) return;

    const rootEl = profileScrollRef.current;
    const target = recentSentinelRef.current;
    if (!rootEl || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRecent();
      },
      { root: rootEl, rootMargin: "240px", threshold: 0.01 }
    );

    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, recentHasMore, recentLastVisible, recentLoadingMore]);

  useEffect(() => {
    if (!open) return;
    if (view !== "post") return;
    if (!selectedPost?.id) return;

    setLoadingComments(true);

    const commentsRef = collection(db, "mmPosts", selectedPost.id, "comments");
    const q = query(commentsRef, orderBy("createdAt", "desc"), limit(100));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data();
          const likes = data.likes || {};
          const isLiked = currentUser ? !!likes[currentUser.uid] : false;
          return { id: d.id, ...data, isLiked };
        });
        setPostComments(list);
        setLoadingComments(false);
      },
      (err) => {
        console.error("Failed to load post comments in profile:", err);
        setPostComments([]);
        setLoadingComments(false);
      }
    );

    return () => unsub();
  }, [open, view, selectedPost?.id, currentUser?.uid]);

  const handleOpenPost = (p) => {
    if (!p) return;

    if (profileScrollRef.current) {
      savedProfileScrollTopRef.current =
        profileScrollRef.current.scrollTop || 0;
    }

    const likes = p.likes || {};
    const isLiked = currentUser ? !!likes[currentUser.uid] : false;

    setSelectedPost({ ...p, isLiked });
    setView("post");
  };

  const handleBackToProfile = () => {
    setView("profile");
    setSelectedPost(null);
    setPostComments([]);
    setCommentText("");

    requestAnimationFrame(() => {
      const el = profileScrollRef.current;
      if (el) el.scrollTop = savedProfileScrollTopRef.current || 0;
    });
  };

  const toggleSelectedPostLike = async () => {
    if (!currentUser || !selectedPost?.id || postBusy) return;

    const newIsLiked = !selectedPost.isLiked;
    const delta = newIsLiked ? 1 : -1;

    setSelectedPost((prev) => ({
      ...prev,
      isLiked: newIsLiked,
      likeCount: (prev.likeCount || 0) + delta,
    }));

    setPostBusy(true);
    try {
      const postRef = doc(db, "mmPosts", selectedPost.id);
      const field = `likes.${currentUser.uid}`;

      if (newIsLiked) {
        await updateDoc(postRef, { [field]: true, likeCount: increment(1) });
      } else {
        await updateDoc(postRef, {
          [field]: deleteField(),
          likeCount: increment(-1),
        });
      }
    } catch (e) {
      console.error("Failed to toggle post like:", e);
      setSelectedPost((prev) => ({
        ...prev,
        isLiked: !newIsLiked,
        likeCount: (prev.likeCount || 0) - delta,
      }));
    } finally {
      setPostBusy(false);
    }
  };

  const commentOver = commentText.length > 88;

  const submitCommentInProfile = async () => {
    if (!currentUser || !selectedPost?.id) return;
    if (!commentText.trim()) return;
    if (commentOver) return;

    setSubmittingComment(true);
    try {
      const displayName =
        currentUserProfile?.name ||
        currentUserProfile?.registrationName ||
        currentUser.displayName ||
        "名無し";

      const avatarUrl =
        currentUserProfile?.avatar || currentUser.photoURL || "";

      const commentsRef = collection(
        db,
        "mmPosts",
        selectedPost.id,
        "comments"
      );
      await addDoc(commentsRef, {
        userId: currentUser.uid,
        userName: displayName,
        userAvatar: avatarUrl,
        text: commentText.trim(),
        likeCount: 0,
        likes: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const postRef = doc(db, "mmPosts", selectedPost.id);
      await updateDoc(postRef, { commentCount: increment(1) });

      setSelectedPost((prev) => ({
        ...prev,
        commentCount: (prev.commentCount ?? 0) + 1,
      }));

      setCommentText("");
    } catch (e) {
      console.error("Failed to submit comment in profile:", e);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={ProfileTransition}
      keepMounted
      fullScreen={isXs}
      fullWidth={!isXs}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isXs ? "100vw" : 600,
          maxWidth: 600,
          height: isXs ? "100dvh" : "auto",
          m: 0,
          bgcolor: surface,
          borderRadius: isXs ? 0 : 2,
          boxShadow: "0 18px 45px rgba(15,10,40,0.35)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          fontWeight: 900,
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {view === "post" ? (
          <IconButton onClick={handleBackToProfile} size="small">
            <KeyboardArrowLeftRoundedIcon />
          </IconButton>
        ) : (
          <Box sx={{ width: 36 }} />
        )}

        <Typography sx={{ fontWeight: 900 }}>
          {view === "post" ? "投稿の詳細" : "プロフィール"}
        </Typography>

        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} size="small">
          とじる
        </Button>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          height: "calc(100dvh - 56px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* PROFILE VIEW */}
        <Box
          ref={profileScrollRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            pt: 2,
            pb: 2.5,
            display: view === "profile" ? "block" : "none",
          }}
        >
          {loading ? (
            <Box
              sx={{
                py: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                プロフィールを読み込み中…
              </Typography>
            </Box>
          ) : (
            <>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Avatar
                  src={avatarUrl}
                  sx={{
                    width: 64,
                    height: 64,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                  }}
                >
                  {displayName.charAt(0)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 900, color: textBase }}
                    noWrap
                  >
                    {displayName}
                  </Typography>

                  {prefecture && (
                    <Chip
                      label={prefecture}
                      size="small"
                      sx={{
                        mt: 0.6,
                        bgcolor: "rgba(0,0,0,0.04)",
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
              </Stack>

              <Box
                sx={{
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.02)",
                  p: 1.5,
                  mb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 900,
                    opacity: 0.7,
                    mb: 0.6,
                    display: "block",
                  }}
                >
                  ひとこと
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    minHeight: 40,
                    opacity: bio ? 1 : 0.6,
                  }}
                >
                  {bio || "自己紹介メッセージはまだ登録されていません。"}
                </Typography>
              </Box>

              {user?.ddpProfileEnabled && (
                <Box
                  sx={{
                    borderRadius: 2,
                    bgcolor: "rgba(0,0,0,0.02)",
                    p: 1.5,
                    mb: 2,
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 900,
                      opacity: 0.7,
                      mb: 0.6,
                      display: "block",
                    }}
                  >
                    DDP
                  </Typography>

                  {ddpDisplayLoading ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ py: 0.5 }}
                    >
                      <CircularProgress size={18} />
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        DDPを読み込み中…
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        opacity: ddpText ? 1 : 0.65,
                        lineHeight: 1.8,
                      }}
                    >
                      {ddpText || "DDPはまだ登録されていません。"}
                    </Typography>
                  )}
                </Box>
              )}

              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 900, mb: 1, color: textBase }}
              >
                最近の投稿
              </Typography>

              {recentLoading ? (
                <Box sx={{ py: 2, textAlign: "center" }}>
                  <CircularProgress size={22} />
                </Box>
              ) : recentPosts.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  まだ投稿はありません。
                </Typography>
              ) : (
                <>
                  <Stack spacing={1.2}>
                    {recentPosts.map((p) => (
                      <RecentPostRowNoImage
                        key={p.id}
                        post={p}
                        onOpen={() => handleOpenPost(p)}
                      />
                    ))}
                  </Stack>

                  <Box
                    ref={recentSentinelRef}
                    sx={{ height: recentHasMore ? 36 : 0 }}
                  />

                  {recentLoadingMore && (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  )}

                  {!recentHasMore &&
                    !recentLoadingMore &&
                    recentPosts.length > 0 && (
                      <Box sx={{ textAlign: "center", py: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.6 }}>
                          すべて読み込みました
                        </Typography>
                      </Box>
                    )}
                </>
              )}
            </>
          )}
        </Box>

        {/* POST DETAIL VIEW */}
        <Box
          sx={{
            flex: 1,
            display: view === "post" ? "flex" : "none",
            flexDirection: "column",
            height: "calc(100dvh - 56px)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, overflowY: "auto", px: 2, pt: 2, pb: 2 }}>
            {!selectedPost ? (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Avatar src={selectedPost.userAvatar || avatarUrl || ""}>
                    {(selectedPost.userName || displayName || "？").charAt(0)}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      {selectedPost.userName || displayName || "名無し"}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {formatTimestamp(selectedPost.createdAt)}
                    </Typography>
                  </Box>
                </Stack>

                {selectedPost.text && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1.2,
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {renderTextWithLinks(selectedPost.text)}
                  </Typography>
                )}

                {selectedPost.linkUrl && (
                  <Box
                    component={MuiLink}
                    href={selectedPost.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      display: "block",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid rgba(0,0,0,0.08)",
                      mt: 1.5,
                      p: 1.2,
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {safeHostnameFromUrl(selectedPost.linkUrl)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 800, mt: 0.3 }}
                    >
                      {selectedPost.linkUrl}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1.2 }}
            >
              <IconButton
                size="small"
                onClick={toggleSelectedPostLike}
                disabled={!currentUser || postBusy}
              >
                {selectedPost?.isLiked ? (
                  <FavoriteRoundedIcon
                    sx={{ color: theme.railActive || "#5A35FF" }}
                    fontSize="small"
                  />
                ) : (
                  <FavoriteBorderRoundedIcon fontSize="small" />
                )}
              </IconButton>
              <Typography variant="body2">
                {selectedPost?.likeCount || 0}
              </Typography>

              <ChatBubbleOutlineRoundedIcon
                sx={{ fontSize: 18, opacity: 0.65, ml: 1 }}
              />
              <Typography variant="body2">
                {selectedPost?.commentCount ?? 0}
              </Typography>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {loadingComments ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : postComments.length === 0 ? (
              <Typography
                variant="caption"
                sx={{ opacity: 0.7, display: "block" }}
              >
                まだコメントはありません。
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ pb: 1 }}>
                {postComments.map((c) => (
                  <Stack
                    key={c.id}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Avatar
                      src={c.userAvatar || ""}
                      sx={{ width: 28, height: 28, mt: 0.2 }}
                    >
                      {(c.userName || "？").charAt(0)}
                    </Avatar>

                    <Box
                      sx={{
                        flex: 1,
                        px: 1,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: theme.appBg || "rgba(0,0,0,0.03)",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="caption" sx={{ fontWeight: 900 }}>
                          {c.userName || "名無し"}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.6 }}>
                          {formatTimestamp(c.createdAt)}
                        </Typography>
                      </Stack>

                      {c.text && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.3,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {renderTextWithLinks(c.text)}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              borderTop: "1px solid rgba(0,0,0,0.06)",
              bgcolor: surface,
              px: 2,
              py: 1,
            }}
          >
            {currentUser ? (
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <Avatar
                  src={currentUserProfile?.avatar || currentUser.photoURL || ""}
                  sx={{ width: 28, height: 28, mb: 0.4 }}
                />
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={3}
                    placeholder="コメント（88文字まで）"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    inputProps={{ maxLength: 88 }}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                      bgcolor: "rgba(0,0,0,0.03)",
                      borderRadius: 2,
                    }}
                  />
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 0.5 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: commentOver ? "error.main" : "text.secondary",
                      }}
                    >
                      {commentText.length}/88
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={submitCommentInProfile}
                      disabled={
                        submittingComment || !commentText.trim() || commentOver
                      }
                    >
                      <SendRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                コメントするにはログインが必要です。
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ==========================================================
    🧩 プロフィール「最近の投稿」1行カード（画像ゼロ版）
   ========================================================== */
function RecentPostRowNoImage({ post, onOpen }) {
  const text = (post.text || "").trim();

  return (
    <Card
      elevation={0}
      onClick={onOpen}
      sx={{
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.10)",
        overflow: "hidden",
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
      }}
    >
      <CardContent sx={{ py: 1.2, px: 1.2 }}>
        <Typography variant="caption" sx={{ opacity: 0.65 }}>
          {formatTimestamp(post.createdAt)}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 0.6,
            fontWeight: 900,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {text || "（テキストなし）"}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.8 }}>
          <FavoriteRoundedIcon sx={{ fontSize: 16, opacity: 0.65 }} />
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {post.likeCount || 0}
          </Typography>
          <ChatBubbleOutlineRoundedIcon
            sx={{ fontSize: 16, opacity: 0.6, ml: 1 }}
          />
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {post.commentCount ?? 0}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
